import type { Translations } from './it'

export const en: Translations = {
  common: {
    ctaLabel: "Let’s have a chat",
    freeNote: "A free 15-minute consultation, no strings attached.",
  },
  header: {
    backToTop: "Back to top",
    logoAlt: "Fra un viaggio e l’altro — Francesca Zaccari, travel designer",
    logoAltShort: "Fra un viaggio e l’altro",
    mainNavAria: "Main navigation",
    mobileNavAria: "Mobile navigation",
    openMenu: "Open menu",
    closeMenu: "Close menu",
    languageLabel: "Change language",
    nav: {
      about: "About",
      services: "Services",
      itineraries: "Itineraries",
      how: "How it works",
      reviews: "Reviews",
    },
  },
  hero: {
    eyebrow: "Travel Designer · Tour Escort",
    greetingBefore: "Hi, my name is ",
    greetingAccent: "Francesca",
    titleBefore: "Your journey",
    titleAccent: "through art.",
    paragraph:
      "My name is Francesca, I’m a Travel Designer and I build tailor-made itineraries that read a place through its art and its history. No “pre-packaged” trips — just journeys that leave you with something.",
    paragraphMobile:
      "I’m a Travel Designer and I build tailor-made itineraries that read a place through its art and its history. No “pre-packaged” trips — just journeys that leave you with something.",
    colosseumAlt: "The Colosseum in Rome at sunset",
    positanoAlt: "The colourful houses of Positano overlooking the sea",
    polaroidCaption: "Amalfi Coast",
    badge: "art journeys around the world",
    girlAlt: "Illustration of a traveller with an orange scarf holding an art book",
  },
  about: {
    eyebrow: "About me",
    titleBefore: "Hi, I’m Francesca,",
    titleAccent: "an art historian on the road.",
    p1: "I studied at the University of Bologna, where I earned two degrees: Cultural Heritage and Visual Arts. In other words, I spent years learning to look at cities the way you look at a painting — and I never stopped.",
    p2: "After graduating I lived in Canada for a year, then came back and took the Travel Designer course in Milan: that’s where I learned the craft of building tailor-made itineraries, piece by piece. In Ravenna I earned my tour escort licence.",
    p3Before:
      "Today I work as a tour escort on food tours for several agencies and I lead groups abroad.",
    p3Brand: "Fra un viaggio e l’altro",
    p3After:
      "is my personal project: young and made with care, just like the tailor-made itineraries I design.",
    portraitAlt: "Francesca Zaccari, travel designer and tour escort",
    portraitMissing: "photo coming soon",
    mapAlt: "Travel map with notes and hand-drawn itineraries",
    mapCaption: "route notes",
    badgeAria: "Certified Travel Designer",
    steps: [
      { place: "Bologna", text: "Two degrees in Cultural Heritage and Visual Arts" },
      { place: "Canada", text: "A year living abroad after graduation" },
      { place: "Milano", text: "Travel Designer course: tailor-made itineraries" },
      { place: "Ravenna", text: "Tour escort licence" },
    ],
  },
  concept: {
    eyebrow: "The concept",
    titleBefore: "Not a package.",
    titleAccent: "A point of view.",
    paragraph:
      "I bring together travel, art and culture: every itinerary tells the story of a place through an artistic or historical lens. Not a list of things to see, but a story to walk through — at the right pace, with context, and a few detours that are worth the trip.",
    points: [
      {
        title: "Cultural and artistic itineraries",
        text: "Every journey has its own reading key: an artist, an era, a movement.",
      },
      {
        title: "For international travellers and groups",
        text: "I design and lead journeys for those arriving from afar and for those travelling together.",
      },
      {
        title: "An art historian’s method",
        text: "Sources, context, details: the same care you put into studying a work of art.",
      },
    ],
    badge: "sample itinerary",
    cardTitle: "In Caravaggio’s footsteps",
    cardText:
      "An itinerary between Rome and Naples to explore the two cities through the painter’s eyes: the canvases in the churches, the streets he walked, the light that changed painting forever.",
    cardMeta: "Rome → Naples · art · history",
    galleryAlt: "Interior of an Italian art gallery with paintings on the walls",
    cinqueTerreAlt: "The colourful villages of the Cinque Terre overlooking the sea",
    discoverCta: "Discover more",
  },
  services: {
    eyebrow: "Services",
    titleBefore: "What I can do",
    titleAccent: "for you",
    items: [
      {
        title: "Travel Design",
        description:
          "A fully personalised travel itinerary, built around who you are, what you’re interested in and how you like to travel. No pre-packaged trips: I research stops, timings and experiences just for you. Bookings are then handled through a travel agency — you don’t have to think about a thing.",
      },
      {
        title: "Tour escorting",
        description:
          "Beyond designing the itinerary, I can personally accompany you or your group, in Italy and abroad. Museums, art cities, routes off the beaten track, food culture: I travel with you and make sure the logistics of the journey work.",
      },
      {
        title: "Itinerary review",
        description:
          "Already have a travel plan? I’ll look it over with an expert eye and tell you honestly what works, what doesn’t and what’s missing: stops that are too rushed, timings that don’t add up, hidden gems you risk missing just steps from where you’ll be.",
      },
      {
        title: "Travel guides",
        description:
          "Travel content and resources to read between one trip and the next: guides, practical tips and art insights, so you arrive prepared and see every place with new eyes.",
      },
    ],
    bottomText: "Not sure where to start?",
    discoverCta: "Discover the services",
  },
  servicesPage: {
    eyebrow: "Services in detail",
    titleBefore: "Four ways to travel",
    titleAccent: "with me.",
    intro:
      "Every journey is different, and every traveller needs something different: some want an itinerary built from scratch, some want a guide by their side, some just want a second opinion. Here you’ll find what I can do for you in detail — with a concrete example for each service.",
    exampleLabel: "A concrete example",
    idealForLabel: "Who it’s for",
    items: [
      {
        example:
          "A week in Paris on the trail of the Impressionists: the Musée d’Orsay early in the morning, away from the crowds; a walk through Montmartre, where these painters lived and worked; a day in Giverny, among Monet’s gardens. A relaxed pace, with time for historic cafés and Rive Gauche bookshops.",
        idealFor: "For those who want a journey built around them, without thinking about a thing.",
      },
      {
        example:
          "Three days in Florence with a group of friends: the Uffizi told slowly, room by room; the artisan Oltrarno, away from the beaten track; a sunset from a panoramic spot you won’t find in guidebooks. I travel with you and share the story of what we see, as an art historian.",
        idealFor: "For groups, families and travellers who want an expert guide by their side.",
      },
      {
        example:
          "Planned Rome in two days with eight stops a day? I’ll recalibrate it with you: cutting pointless dashes across the city, reordering visits by area, and adding that church with two Caravaggios you risked missing two steps from your hotel.",
        idealFor: "For those who already have a travel plan but want an expert eye before leaving.",
      },
      {
        example:
          "A tailor-made digital guide before departure: what to see and in what order, how to get around, where to eat and the art keys to every stop — to consult offline, straight from your phone.",
        idealFor: "For independent travellers who want to arrive prepared.",
      },
    ],
    ctaTitle: "Not sure which one is right for you?",
    ctaSubtitle:
      "Tell me about it in a free 15-minute chat: we’ll figure out where to start together.",
    backHome: "Back to home",
  },
  itinerariesPage: {
    eyebrow: "Art & culture travel itineraries",
    titleBefore: "Journeys with",
    titleAccent: "a point of view.",
    intro: "These are examples of itineraries I can build and personalise with you.",
    detailsLabel: "The experience",
    durationLabel: "Suggested duration",
    backHome: "Back to home",
    closeModalAria: "Close itinerary details",
    itineraryLabel: "Itinerary",
    discoverMore: "Discover more",
    ctaEyebrow: "Let's build your journey",
    ctaTitle: "Already have a destination in mind?",
    ctaText: "Let’s start from what you want and turn it into a tailor-made itinerary.",
    items: [
      { title: "Caravaggio between Rome and Naples", description: "Churches, squares and masterpieces told through the painter’s light and story.", details: "A journey that begins in Rome and shows us the painter’s early years among the Capitoline Museums, the churches and the Borghese Gallery, before arriving in Naples, where Caravaggio finally feels understood.", duration: "5 days", cover: "/images/caravaggio-giuditta.jpg", coverAlt: "Judith Beheading Holofernes, Caravaggio" },
      { title: "Impressionists in Provence", description: "From Cézanne to Van Gogh, Provence and its blooming lavender fields seen through their eyes.", details: "An itinerary through the cities that inspired the great Impressionist masters, to read a landscape that changed the way we see art.", duration: "5–7 days", cover: "/uploads/eabb27de4d.jpg", coverAlt: "Wheat field with a view of Saint-Rémy, Van Gogh" },
      { title: "In the footsteps of the Abbey of Pomposa", description: "Three one-day itineraries, one common thread: the Abbey of Pomposa, a beacon of faith and culture in the Middle Ages between the Po and the sea.", details: "1 · Giotto between Padua and Pomposa\nFrom Padua, between the Scrovegni Chapel and the Basilica of Saint Anthony, all the way to Pomposa: following the painter’s influences along the Romagna coast.\n\n2 · Mosaic between Ravenna and Pomposa\nWe set off from the Byzantine and paleo-Christian mosaics of Ravenna and Sant’Apollinare in Classe, arriving in Pomposa before the medieval mosaic pavement: a journey that closes a circle opened centuries before.\n\n3 · Dante, exile and the last days\nFrom Ravenna, through the places that welcomed him while he wrote the Divine Comedy, to Pomposa, where he met his personal inferno: there he falls ill and soon after dies.", duration: "3 itineraries · 1 day each", cover: "/uploads/252221080f.jpg", coverAlt: "Interior of the basilica with medieval frescoes" },
      { title: "Renaissance Florence", description: "The city of the Medici through artists, workshops and architecture.", details: "The Uffizi, Oltrarno, chapels and artisan workshops at a balanced pace, with the right pauses.", duration: "2–4 days", cover: "/uploads/e89d5c2b97.jpg", coverAlt: "Panorama of Florence with the Duomo" },
      { title: "Venice between art and water", description: "A Venice to explore with curiosity, among masterpieces and hidden details.", details: "San Marco, Dorsoduro, lagoon islands and alternative routes to discover the city without rushing.", duration: "3 days", cover: "/images/venezia.jpg", coverAlt: "A Venice canal" },
      { title: "Cinque Terre beyond the postcard", description: "Villages, trails and Ligurian landscapes at a slow, thoughtful pace.", details: "A journey between sea, local culture and small producers, away from the busiest stops.", duration: "3–5 days", cover: "/images/cinque-terre.jpg", coverAlt: "The colourful villages of Cinque Terre" },
    ],
  },
  collaborations: {
    navLabel: "Collaborations",
    eyebrow: "Collaborations",
    titleBefore: "The companies that",
    titleAccent: "travel with me.",
    intro:
      "Alongside tailor-made journeys, I work with tour operators and local companies that share my way of travelling. Here you’ll find all the tours and groups I depart with, bookable directly with them.",
    backHome: "Back to home",
    homeEyebrow: "Collaborations",
    homeTitleBefore: "I also travel",
    homeTitleAccent: "in company.",
    homeText:
      "Food tours in Emilia-Romagna, group tours across Italy and around the world, travel coordinator: some of my departures can be booked directly with the companies I collaborate with.",
    homeCta: "Discover the collaborations",
    foodTours: {
      title: "Food tours",
      role: "Tour escort",
      intro:
        "Food is culture: I lead food tours across Emilia-Romagna, through historic markets, artisan shops and laid tables. Every tour is bookable directly with the organiser.",
      partners: [
        {
          place: "Rimini · with Hili",
          text: "In Rimini I walk with Hili through the highlights of the old town: fresh fish, piadina and the stories of the city, in a tour that blends taste and curiosity.",
          code: "",
          codeLabel: "",
          links: [
            { label: "Get Your Guide", url: "https://gyg.me/GOCSOAtk" },
            { label: "Viator", url: "https://www.viator.com/it-IT/tours/Rimini/Rimini-Walking-Food-Tour-and-Highlights/d30027-326991P39" },
          ],
        },
        {
          place: "Bologna · with Do Eat Better",
          text: "In Bologna I lead the Do Eat Better Experience food tour: the flavours of the city told among the market stalls and the shops of the centre. With my code you get a 5% discount.",
          code: "FRANCIZ5BOLOGNA",
          codeLabel: "5% discount code: ",
          links: [
            { label: "Do Eat Better Experience", url: "https://doeatbetterexperience.com/italy-food-tour/what-to-do-in-bologna/bologna-food-tour/" },
          ],
        },
        {
          place: "Bologna · with Tours and The City",
          text: "With Tours and The City I lead the Bologna Gastronomic Experience: historic markets, artisan shops and traditional recipes told around the table.",
          code: "",
          codeLabel: "",
          links: [
            { label: "Tours and The City", url: "https://toursandthecity.com/bologna-gastronomic-experience-with-a-local/" },
          ],
        },
        {
          place: "Bologna · with Taste Bologna",
          text: "With Taste Bologna I lead the Classic Bologna Food Tour: historic osterie, markets and family shops, tasting Parmigiano, mortadella and handmade tortellini in small groups.",
          code: "",
          codeLabel: "",
          links: [
            { label: "Taste Bologna", url: "https://www.tastebologna.net/classic-bologna" },
          ],
        },
      ],
    },
    village: {
      title: "The Village",
      role: "Tour escort",
      text: "For The Village I escort tours in Emilia-Romagna, departing from Bologna and Ravenna: short, carefully crafted trips to discover the wonders of the region.",
      linkLabel: "The Village Italy",
      url: "https://www.thevillageitaly.com/",
    },
    realWanderlust: {
      title: "Real Wanderlust",
      role: "Group trip coordinator",
      text: "I escort Real Wanderlust group trips all over the world, between carefully crafted itineraries and a free spirit.",
      linkLabel: "Real Wanderlust",
      url: "https://www.realwanderlust.it/",
    },
    theVoyage: {
      title: "The Voyage",
      role: "Bologna local expert",
      text: "With The Voyage I show Bologna to travellers from all over the world: tours through the porticoes, the market and the stories of one of Italy’s most beautiful cities.",
      linkLabel: "My profile on The Voyage",
      url: "https://www.thevoyage.co/locals/francesca-zaccari",
    },
    aurea: {
      title: "Aurea Viaggi",
      role: "Tour escort",
      text: "With Aurea Viaggi I escort slow-paced group trips: art and nature destinations enjoyed unhurried, with comfortable timing and carefully crafted storytelling, perfect for anyone who prefers to savour each stop.",
      linkLabel: "Aurea Viaggi",
      url: "https://aureaviaggicastenaso.my.canva.site/",
      tour: {
        eyebrow: "Upcoming tour",
        title: "Marmore Falls, a journey about water",
        text: "From 9 to 11 April 2027 we explore the Marmore Falls together, celebrated by poets through water: land, art and nature.",
        email: "fra.unviaggioelaltro@gmail.com",
        emailSubject: "Marmore Falls tour \u2014 Aurea Viaggi, 9-11 April",
        emailLabel: "Email me for more info",
      },
    },
    weroad: {
      title: "WeRoad",
      role: "Group trip coordinator",
      text: "On WeRoad I travel as a trip coordinator: groups of peers, itineraries designed to experience the destination together and a pinch of adventure.",
      linkLabel: "Upcoming trips — coordinator profile",
      url: "https://www.weroad.it/coordinatori/francesca-zaccari",
    },
  },
  process: {
    eyebrow: "The process",
    titleBefore: "How it",
    titleAccent: "works",
    steps: [
      {
        title: "We get to know each other",
        description:
          "A free fifteen-minute consultation: you tell me where you want to go, what you love and how you like to travel. No strings attached — it’s how we find out if I can help.",
      },
      {
        title: "I design your journey",
        description:
          "I build your tailor-made itinerary: stops, experiences, pace. No copy-paste — every journey starts from scratch and from the person who will live it.",
      },
      {
        title: "Off you go",
        description:
          "Your itinerary is ready. Bookings are handled through a travel agency — all you have to do is pack your bags and leave.",
      },
    ],
  },
  reviews: {
    eyebrow: "Reviews",
    titleBefore: "Words from those who travelled",
    titleAccent: "with me",
    prevAria: "Previous review",
    nextAria: "Next review",
    goToAria: "Go to review {n}",
    items: [
      {
        name: "Noemi G.",
        initial: "N",
        trip: "Budapest, 3 tailor-made days",
        text: "Francesca really understood my needs. After deciding together which of Budapest’s iconic sights and attractions to visit, she created a tailor-made 3-day itinerary. The little changes we had to make along the way (like swapping a couple of visits between day two and day three) didn’t spoil the experience at all. On the contrary, they let me follow my own pace and enjoy the capital to the fullest.",
      },
      {
        name: "Giulia Z.",
        initial: "G",
        trip: "Ireland tour in the making",
        text: "I’ve been living in Dublin for 11 months now, but I’d never had the chance to explore Ireland properly. So I turned to Francesca, who is putting together a tour based on my needs and preferences. She keeps me updated on every change and development. She understood exactly the kind of experience I want to have and is planning everything with great passion and dedication. At this point I can’t wait to see all the places Francesca will have uncovered among Ireland’s breathtaking landscapes.",
      },
      {
        name: "Soana G.",
        initial: "S",
        trip: "Detailed quote",
        text: "I have to compliment Francesca because her quote was incredibly detailed and cared for down to the smallest details... She's really, really good — I recommend her to anyone dreaming of a trip, because she works to make your dream come true and turn the journey into a truly unique experience.",
      },
      {
        name: "Luca C.",
        initial: "L",
        trip: "Vietnam and Philippines, itinerary review",
        text: "I had the free consultation with Francesca and then decided to go for the itinerary review. Even though my trip to Vietnam and the Philippines was organised very last-minute, with travel and hotels booked just days before leaving, she managed to reorganise the itinerary in the best possible way and we got to see everything we wanted, optimally. Her advice was essential for the flight connections, and based on that we figured out how to get around.",
      },
      {
        name: "Marica D.",
        initial: "M",
        trip: "Bologna, city tour with escort",
        text: "I first met Francesca during a group trip in August and, after that first experience, I had the pleasure of finding her again as my tour escort discovering Bologna, while I was there for work. It was a wonderful experience: Francesca guided me through the city’s main points of interest, telling me about its curiosities with passion and expertise. The visit was pleasant and never banal, also thanks to her very helpful manner and her ability to put everyone at ease. And of course the food was not missing: we tasted several specialities of Bolognese cuisine, which made the day even more complete and authentic. It was a real pleasure spending time with Francesca and discovering Bologna through her stories and her point of view. A knowledgeable, sunny and passionate person, whom I absolutely recommend to anyone who wants to get to know Bologna not only as a tourist, but also through its stories, traditions and cuisine.",
      },
    ],
  },
  ctaBand: {
    title: "Got a trip in mind?",
    subtitle: "Tell me about it in a free 15-minute consultation.",
  },
  finalCta: {
    quote:
      "[…] Beneath the deep blue of the sky a seabird drifts away; it never rests: for every image carries the words: “further on”.",
    titleBefore: "Ready to go?",
    titleAccent: "Your journey starts here.",
    subtext: "No purchase commitment.",
    polaroidCaption: "New points of view",
    positanoAlt: "A breathtaking panorama discovered while travelling",
    veneziaAlt: "A Venice canal at sunset",
  },
  privacy: {
    backLink: "Back to home",
    seoTitle: "Privacy Policy — Fra un viaggio e l’altro",
    title:
      "PRIVACY NOTICE FOR THE PROCESSING OF PERSONAL DATA PURSUANT TO ARTICLE 13 OF EU REGULATION 2016/679",
    intro:
      "This Privacy Policy describes how Fra un viaggio e l’altro collects, uses, protects and shares the personal data of users visiting the website www.fraunviaggioelaltro.it, in full compliance with EU Regulation 2016/679 (GDPR) and Italian data protection law.",
    sections: [
      {
        heading: "Data controller",
        paragraphs: ["The data controller is:"],
        bullets: ["Fra un viaggio e l’altro", "Francesca Zaccari", "fra.unviaggioelaltro@gmail.com"],
        outro: "",
      },
      {
        heading: "Types of data collected",
        paragraphs: [
          "While browsing and/or using the services offered by the website, we may collect the following categories of data:",
        ],
        bullets: [
          "Identification data: name, surname, email, telephone number (e.g. contact form or information request).",
          "Browsing data: IP address, browser type, session data, pages visited (via technical or analytical cookies).",
          "Data provided voluntarily: messages, comments, reviews, bookings, newsletter subscription.",
        ],
        outro: "",
      },
      {
        heading: "Purpose of the processing",
        paragraphs: ["The personal data collected is processed for the following purposes:"],
        bullets: [
          "Responding to requests sent via the contact form;",
          "Providing information or quotes about trips and tourist services;",
          "Subscribing the user to the newsletter (with prior consent);",
          "Improving the user experience and website features;",
          "Complying with legal or tax obligations.",
        ],
        outro: "",
      },
      {
        heading: "Legal basis of the processing",
        paragraphs: ["The processing of data is based on:"],
        bullets: [
          "Consent expressed by the data subject (e.g. newsletter subscription);",
          "Performance of pre-contractual or contractual measures (e.g. a request for a quote or information);",
          "Legal obligations to which the Data Controller is subject;",
          "Legitimate interest of the Data Controller, in the case of anonymous statistical analysis to improve services.",
        ],
        outro: "",
      },
      {
        heading: "Method of processing",
        paragraphs: [
          "Data is processed lawfully, fairly and transparently, using manual and computer tools. Appropriate technical and organisational measures are adopted to ensure the security and confidentiality of the data, preventing unauthorised access, loss or disclosure.",
        ],
        bullets: [],
        outro: "",
      },
      {
        heading: "Communication and dissemination of data",
        paragraphs: ["Personal data will never be shared with third parties, except in the following cases:"],
        bullets: [
          "Parties providing services on behalf of the Data Controller (e.g. web hosting, newsletter management, IT consultants);",
          "Legal obligations, judicial authorities or public administrations;",
          "Collaborators or partners closely involved in the management of the services offered (e.g. travel agencies or tour operators).",
        ],
        outro:
          "All external parties who process data on behalf of the Data Controller are appointed as Data Processors and guarantee adequate protection measures.",
      },
      {
        heading: "Data retention",
        paragraphs: [
          "Data will be retained for the time strictly necessary to achieve the purposes indicated, unless otherwise required by law. In particular:",
        ],
        bullets: [
          "Contact data: until the request is fulfilled or consent is withdrawn;",
          "Newsletter data: until unsubscribing;",
          "Browsing data: according to the timeframes defined in the Cookie Policy.",
        ],
        outro: "",
      },
      {
        heading: "Data subject’s rights",
        paragraphs: [
          "Pursuant to Articles 15-22 of the GDPR, users may exercise the following rights at any time:",
        ],
        bullets: [
          "Access to their personal data;",
          "Rectification or updating;",
          "Erasure (the “right to be forgotten”);",
          "Restriction of processing;",
          "Objection to processing;",
          "Data portability;",
          "Withdrawal of consent (without prejudice to the lawfulness of the previous processing);",
          "Lodging a complaint with the Italian Data Protection Authority (Garante per la Protezione dei Dati Personali).",
        ],
        outro:
          "To exercise your rights, you can send a request to the email address fra.unviaggioelaltro@gmail.com.",
      },
      {
        heading: "Transfer of data outside the EU",
        paragraphs: [
          "As a rule, data is not transferred outside the European Economic Area. Where necessary, any transfer will take place in accordance with the safeguards provided for by the GDPR (e.g. adequacy decisions, standard contractual clauses).",
        ],
        bullets: [],
        outro: "",
      },
      {
        heading: "Cookie Policy",
        paragraphs: [
          "This website uses technical cookies and, with prior consent, profiling or analytical cookies as well. For more information and to manage your preferences, please refer to the dedicated Cookie Policy.",
        ],
        bullets: [],
        outro: "",
      },
      {
        heading: "Changes to this notice",
        paragraphs: [
          "Fra un viaggio e l’altro reserves the right to amend this Privacy Policy at any time by publishing the updated version on the website. Users are encouraged to check this page regularly.",
        ],
        bullets: [],
        outro: "",
      },
    ],
  },
  notFound: {
    title: "Page not found",
    message: "The page {path} doesn't exist.",
    goHome: "Back to home",
    goBack: "Go back",
  },
  footer: {
    privacyLink: "Privacy Policy",
    logoAlt: "Fra un viaggio e l’altro — logo",
    copyright:
      "© {year} Fra un viaggio e l’altro · Francesca Zaccari — Travel Designer & Tour Escort · VAT 02803860390",
  },
}
